'use client';

import { c as _c } from "react/compiler-runtime";
import Image, { StaticImageData } from 'next/image';
import React from 'react';
import { Cursor } from '@/components/ui/cursor-map';
import irvineImage from '@/images/photos/cities/irvine.jpg';
import losAngelesImage from '@/images/photos/cities/los_angeles.jpg';
import morroBayImage from '@/images/photos/cities/morro_bay.jpg';
import sanFranciscoImage from '@/images/photos/cities/san_francisco.jpg';
import sanLuisObispoImage from '@/images/photos/cities/san_luis_obispo.jpg';
import vegasImage from '@/images/photos/cities/vegas.jpg';
import { useAnalytics } from '@/hooks/useAnalytics';

// Map city names to their images (morro bay is the default fallback)
const cityImages: Record<string, StaticImageData> = {
  irvine: irvineImage,
  'los angeles': losAngelesImage,
  la: losAngelesImage,
  'morro bay': morroBayImage,
  'san francisco': sanFranciscoImage,
  sf: sanFranciscoImage,
  'san luis obispo': sanLuisObispoImage,
  slo: sanLuisObispoImage,
  'las vegas': vegasImage,
  vegas: vegasImage
};
function getCityImage(city: string): StaticImageData {
  const normalizedCity = city.toLowerCase().trim();
  return cityImages[normalizedCity] || morroBayImage;
}
interface LocationCursorMapProps {
  city: string;
  state?: string;
  region?: string;
}
export function LocationCursorMap(t0) {
  const $ = _c(28);
  const {
    city,
    state,
    region
  } = t0;
  const {
    track
  } = useAnalytics();
  const hasInteractedRef = React.useRef(false);
  let t1;
  if ($[0] !== city) {
    t1 = getCityImage(city);
    $[0] = city;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const locationImage = t1;
  let t2;
  if ($[2] !== city || $[3] !== region || $[4] !== state || $[5] !== track) {
    t2 = () => {
      if (!hasInteractedRef.current) {
        track("content_engagement", {
          type: "location_hover",
          details: `${city}${state ? `, ${state}` : ""}${region && region !== "United States" ? `, ${region}` : ""}`
        });
        hasInteractedRef.current = true;
      }
    };
    $[2] = city;
    $[3] = region;
    $[4] = state;
    $[5] = track;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleMouseEnter = t2;
  let t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      initial: {
        height: 0,
        opacity: 0,
        scale: 0.3
      },
      animate: {
        height: "auto",
        opacity: 1,
        scale: 1
      },
      exit: {
        height: 0,
        opacity: 0,
        scale: 0.3
      }
    };
    t4 = {
      type: "spring",
      duration: 0.3,
      bounce: 0.1
    };
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      bounce: 0.01
    };
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = `${city} ${state || ""} ${region || ""}`;
  let t7;
  if ($[10] !== locationImage || $[11] !== t6) {
    t7 = <Image src={locationImage} alt={t6} className="size-40 object-cover" width={160} height={160} unoptimized={true} />;
    $[10] = locationImage;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = state ? `, ${state}` : "";
  const t9 = region && region !== "United States" ? `, ${region}` : "";
  let t10;
  if ($[13] !== city || $[14] !== t8 || $[15] !== t9) {
    t10 = <div className="absolute right-0 bottom-0 left-0 bg-neutral-950/60 p-2 text-xs text-white">{city}{t8}{t9}</div>;
    $[13] = city;
    $[14] = t8;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t7) {
    t11 = <Cursor attachToParent={true} variants={t3} transition={t4} className="overflow-hidden" springConfig={t5}><div className="overflow-hidden rounded-lg border-2 border-white shadow-lg">{t7}{t10}</div></Cursor>;
    $[17] = t10;
    $[18] = t7;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = state ? `, ${state}` : "";
  const t13 = region && region !== "United States" ? `, ${region}` : "";
  let t14;
  if ($[20] !== city || $[21] !== t12 || $[22] !== t13) {
    t14 = <span className="flex items-center">Currently in{" "}<span className="ml-1 border-b border-dashed border-zinc-300 font-medium hover:border-zinc-500 dark:border-zinc-700">{city}{t12}{t13}</span></span>;
    $[20] = city;
    $[21] = t12;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== handleMouseEnter || $[25] !== t11 || $[26] !== t14) {
    t15 = <div className="relative inline-flex cursor-pointer items-center" onMouseEnter={handleMouseEnter}>{t11}{t14}</div>;
    $[24] = handleMouseEnter;
    $[25] = t11;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  return t15;
}